import { Button } from "primereact/button";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { useState, useRef } from "react";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import FilePreviewDialog from "../../components/common/FilePreviewDialog";
import { SplitButton } from "primereact/splitbutton";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";

const ActionButtons = ({ protocolId }: { protocolId: number }) => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(false);
  const [triggerDownloadWorkingProtocol, { isLoading: isDownloadWorkingProtocolLoading }] =
    protocolsApi.useLazyDownloadWorkingProtocolQuery();

  const [triggerDownloadSignedProtocol, { isLoading: isDownloadSignedProtocolLoading }] =
    protocolsApi.useLazyDownloadSignedProtocolQuery();

  const [triggerCheckProtocolSignature, { isLoading: isCheckProtocolSignatureLoading }] =
    protocolsApi.useCheckProtocolSignatureByProtocolIdMutation();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [pdfTitle, setPdfTitle] = useState("");

  const [copyProtocol] = protocolsApi.useCopyProtocolMutation();

  const openPdfInModal = async (downloadFunction: any, title: string) => {
    try {
      const result = await downloadFunction({ protocolId, format: "pdf" }).unwrap();
      console.log("Downloaded PDF:", result);
      const blob = new Blob([result.blob], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(title);
      setShowPdfDialog(true);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const closePdfDialog = () => {
    setShowPdfDialog(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const downloadExcelFile = async () => {
    try {
      const result = await triggerDownloadWorkingProtocol({ protocolId, format: "excel" }).unwrap();
      console.log("Downloaded Excel:", result);
      const blob = new Blob([result.blob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel:", error);
    }
  };

  const checkProtocolSignature = async () => {
    try {
      await triggerCheckProtocolSignature(protocolId).unwrap();
    } catch (error) {
      console.error("Error checking protocol signature:", error);
    }
  };

  const handleCopy = async () => {
    setLoading(true);
    try {
      const response = await copyProtocol(protocolId).unwrap();
      if (response.data?.result) {
        toast.current?.show({ severity: "success", summary: "Копиране", detail: "Протоколът е копиран успешно." });
        navigate(`/protocols/${response.data.result}`);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: response.data?.message || "Възникна грешка по време на копирането",
        });
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Грешка", detail: "Възникна грешка по време на копирането" });
    } finally {
      setLoading(false);
    }
  };

  const confirmCopy = (event: React.MouseEvent) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: 'Сигурни ли сте, че искате да копирате протокола?',
      icon: 'pi pi-exclamation-triangle',
      accept: handleCopy,
      acceptLabel: 'Да',
      rejectLabel: 'Не'
    });
  };

  return (
    <div className="flex flex-row justify-content-end gap-2">
      <div>
        <Button
          type="button"
          icon="pi pi-file"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Печат на списък"
          loading={isDownloadWorkingProtocolLoading}
          onClick={() => openPdfInModal(triggerDownloadWorkingProtocol, "Печат на списък")}
        />
      </div>
      <div>
        <Button
          type="button"
          icon="pi pi-file-excel"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Изтегляне на Excel"
          loading={isDownloadWorkingProtocolLoading}
          onClick={downloadExcelFile}
        />
      </div>
      <div>
        <Button
          type="button"
          icon="pi pi-check"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Проверка за подпис"
          loading={isCheckProtocolSignatureLoading}
          onClick={checkProtocolSignature}
        />
      </div>
      <div>
        <Button
          type="button"
          icon="pi pi-download"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Изтегляне на подписан протокол"
          loading={isDownloadSignedProtocolLoading}
          onClick={() => openPdfInModal(triggerDownloadSignedProtocol, "Подписан протокол")}
        />
      </div>
      <div>
        <Button
          type="button"
          icon="pi pi-copy"
          iconPos="top"
          size="small"
          className="flex-1"
          severity="secondary"
          outlined
          label="Копиране на протокола"
          loading={loading}
          onClick={confirmCopy}
        />
      </div>

      <ConfirmPopup />

      <FilePreviewDialog visible={showPdfDialog} onHide={closePdfDialog} fileUrl={pdfUrl} title={pdfTitle} />
      <Toast ref={toast} />
    </div>
  );
};

export default ActionButtons;
